import type { Building } from '@shared/models/design/building-design.model';
import type { Shape, Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';
import {
  getCutoutShapes,
  getSolidShapes,
} from '@shared/helpers/design/cutout.helpers';
import type { StickyAngleSnapState } from './angle-snap.helpers';

/** Ignore short notches relative to a building's longest exterior segment. */
const MAIN_SEGMENT_MIN_LENGTH_FACTOR = 0.5;
const MIN_SEGMENT_LENGTH_METERS = 1e-6;
const MAIN_DIRECTION_CLUSTER_DEGREES = 1;
const MIN_SHORTCUT_ALIGNMENT_CHANGE_DEGREES = 1;
const BEARING_SNAP_ENTER_DEGREES = 3;
const BEARING_SNAP_BREAKOUT_DEGREES = 6;

type BuildingGeometry = {
  readonly storeys: readonly Storey[];
};

type SegmentDirection = {
  degrees: number;
  length: number;
};

export type BuildingBearingSnapResult = {
  bearing: number;
  snapped: boolean;
  state: StickyAngleSnapState;
};

export type BuildingBearingAlignment = {
  bearing: number;
  direction: number;
};

type RankedSegmentDirection = {
  direction: number;
  prominence: number;
};

type WeightedSegmentDirection = {
  direction: number;
  length: number;
};

const normalizeMainDirection = (degrees: number): number =>
  ((degrees % 90) + 90) % 90;

const getMainDirectionDistance = (left: number, right: number): number => {
  const direct = Math.abs(left - right);
  return Math.min(direct, 90 - direct);
};

/** Length-weighted circular mean for directions whose period is 90°. */
const getWeightedMainDirection = (
  directions: readonly WeightedSegmentDirection[],
): number => {
  const vector = directions.reduce(
    (sum, { direction, length }) => {
      const radians = (direction * 2 * Math.PI) / 90;
      return {
        x: sum.x + Math.cos(radians) * length,
        y: sum.y + Math.sin(radians) * length,
      };
    },
    { x: 0, y: 0 },
  );
  return normalizeMainDirection(
    (Math.atan2(vector.y, vector.x) * 90) / (2 * Math.PI),
  );
};

/** Merge imperceptibly similar axes, including across the 0°/90° boundary. */
const clusterMainSegmentDirections = (
  directions: readonly WeightedSegmentDirection[],
): RankedSegmentDirection[] => {
  const remaining = new Set(directions.map((_, index) => index));
  const clusters: WeightedSegmentDirection[][] = [];

  while (remaining.size) {
    const first = remaining.values().next().value;
    if (first === undefined) break;
    remaining.delete(first);
    const cluster = [directions[first]];
    const pending = [first];
    while (pending.length) {
      const currentIndex = pending.pop();
      if (currentIndex === undefined) break;
      const current = directions[currentIndex];
      for (const candidateIndex of [...remaining]) {
        const candidate = directions[candidateIndex];
        if (
          !current ||
          !candidate ||
          getMainDirectionDistance(current.direction, candidate.direction) >
            MAIN_DIRECTION_CLUSTER_DEGREES
        ) {
          continue;
        }
        remaining.delete(candidateIndex);
        pending.push(candidateIndex);
        cluster.push(candidate);
      }
    }
    clusters.push(cluster.filter((entry) => entry !== undefined));
  }

  return clusters.map((cluster) => ({
    direction: getWeightedMainDirection(cluster),
    prominence: cluster.reduce((sum, { length }) => sum + length, 0),
  }));
};

const getSegmentDirection = (
  start: Point2D,
  end: Point2D,
): SegmentDirection | undefined => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (length <= MIN_SEGMENT_LENGTH_METERS) return undefined;
  return {
    degrees: (Math.atan2(dy, dx) * 180) / Math.PI,
    length,
  };
};

const getShapeSegments = (shape: Pick<Shape, 'points'>): SegmentDirection[] =>
  shape.points.flatMap((point, index, points) => {
    const next = points[(index + 1) % points.length];
    const segment = next ? getSegmentDirection(point, next) : undefined;
    return segment ? [segment] : [];
  });

const appendMainSegmentDirections = (
  directions: WeightedSegmentDirection[],
  segments: readonly SegmentDirection[],
): void => {
  const longest = Math.max(0, ...segments.map((segment) => segment.length));
  if (!longest) return;

  segments
    .filter(
      (segment) => segment.length >= longest * MAIN_SEGMENT_MIN_LENGTH_FACTOR,
    )
    .forEach((segment) => {
      directions.push({
        direction: normalizeMainDirection(segment.degrees),
        length: segment.length,
      });
    });
};

/**
 * Main exterior and cutout segment directions for each building. Directions
 * are returned modulo 90° because parallel and perpendicular walls produce
 * the same horizontal/vertical camera alignments in a top-down view.
 */
const getBuildingMainSegmentDirectionRanking = (
  buildings: readonly BuildingGeometry[],
): RankedSegmentDirection[] => {
  const directions: WeightedSegmentDirection[] = [];

  buildings.forEach((building) => {
    const exteriorSegments = building.storeys.flatMap((storey) =>
      getSolidShapes(storey).flatMap(getShapeSegments),
    );
    appendMainSegmentDirections(directions, exteriorSegments);

    building.storeys.forEach((storey) => {
      getCutoutShapes(storey).forEach((cutout) => {
        appendMainSegmentDirections(directions, getShapeSegments(cutout));
      });
    });
  });

  return clusterMainSegmentDirections(directions);
};

export const getBuildingMainSegmentDirections = (
  buildings: readonly BuildingGeometry[],
): number[] =>
  getBuildingMainSegmentDirectionRanking(buildings).map(
    ({ direction }) => direction,
  );

/** Main directions ordered by the summed length of their qualifying segments. */
export const getRankedBuildingMainSegmentDirections = (
  buildings: readonly BuildingGeometry[],
): number[] =>
  getBuildingMainSegmentDirectionRanking(buildings)
    .sort((left, right) => right.prominence - left.prominence)
    .map(({ direction }) => direction);

/** Resolve the buildings whose axes participate in the current camera drag. */
export const getBearingSnapDirectionsForSelection = (input: {
  hasBuildingSelection: boolean;
  selectedStoreys: readonly Storey[];
  otherBuildings: readonly Building[];
}): number[] =>
  getBuildingMainSegmentDirections(
    input.hasBuildingSelection
      ? [{ storeys: input.selectedStoreys }]
      : input.otherBuildings,
  );

/** Resolve and rank the building axes used by repeated Shift+O presses. */
export const getRankedBearingDirectionsForSelection = (input: {
  hasBuildingSelection: boolean;
  selectedStoreys: readonly Storey[];
  otherBuildings: readonly Building[];
}): number[] =>
  getRankedBuildingMainSegmentDirections(
    input.hasBuildingSelection
      ? [{ storeys: input.selectedStoreys }]
      : input.otherBuildings,
  );

const alignDirectionToNearestQuarterTurn = (
  rawBearing: number,
  direction: number,
): number => direction + Math.round((rawBearing - direction) / 90) * 90;

/** Find the closest bearing that makes a supplied main direction screen-aligned. */
export const alignBearingToNearestBuildingDirection = (
  rawBearing: number,
  directions: readonly number[],
): number | undefined =>
  directions.reduce<{ bearing: number; distance: number } | undefined>(
    (closest, direction) => {
      const aligned = alignDirectionToNearestQuarterTurn(rawBearing, direction);
      const distance = Math.abs(rawBearing - aligned);
      if (!closest || distance < closest.distance) {
        return { bearing: aligned, distance };
      }
      return closest;
    },
    undefined,
  )?.bearing;

/** Next quarter-turn for this direction, always increasing Mapbox bearing. */
const alignDirectionToNextCounterClockwiseQuarterTurn = (
  rawBearing: number,
  direction: number,
): number => {
  let bearing = direction + Math.floor((rawBearing - direction) / 90 + 1) * 90;
  if (bearing - rawBearing < MIN_SHORTCUT_ALIGNMENT_CHANGE_DEGREES) {
    bearing += 90;
  }
  return bearing;
};

/**
 * Advance to the next counterclockwise screen alignment. Because every main
 * direction repeats each quarter-turn, successive calls keep orbiting the
 * building in one direction and present its facades in angular order.
 */
export const getNextBuildingBearingAlignment = (input: {
  rawBearing: number;
  rankedDirections: readonly number[];
}): BuildingBearingAlignment | undefined => {
  const { rawBearing, rankedDirections } = input;
  if (!rankedDirections.length) return undefined;
  return rankedDirections.reduce<BuildingBearingAlignment | undefined>(
    (next, direction) => {
      const bearing = alignDirectionToNextCounterClockwiseQuarterTurn(
        rawBearing,
        direction,
      );
      return !next || bearing < next.bearing ? { direction, bearing } : next;
    },
    undefined,
  );
};

/** Soft-snap a raw bearing to the nearest building-axis alignment. */
export const snapBearingToBuildingDirections = (
  rawBearing: number,
  directions: readonly number[],
  sticky: StickyAngleSnapState = {},
): BuildingBearingSnapResult => {
  const locked = sticky.lockedDeg;
  if (
    locked !== undefined &&
    Math.abs(rawBearing - locked) <= BEARING_SNAP_BREAKOUT_DEGREES
  ) {
    return {
      bearing: locked,
      snapped: true,
      state: { lockedDeg: locked },
    };
  }

  const nearestBearing = alignBearingToNearestBuildingDirection(
    rawBearing,
    directions,
  );
  const nearestDistance =
    nearestBearing === undefined
      ? undefined
      : Math.abs(rawBearing - nearestBearing);

  if (
    nearestBearing === undefined ||
    nearestDistance === undefined ||
    nearestDistance > BEARING_SNAP_ENTER_DEGREES
  ) {
    return { bearing: rawBearing, snapped: false, state: {} };
  }
  return {
    bearing: nearestBearing,
    snapped: true,
    state: { lockedDeg: nearestBearing },
  };
};
